import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  ThemeProvider,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material'
import { useId, useMemo } from 'react'
import { useAppTheme } from '@/theme/ThemeContext'
import type { Calendar, Grain } from '@/alerts/calendar'
import CalendarEditor from './CalendarEditor'

/**
 * The calendar editor in a dialog, for the tree calendar and for one check's own calendar.
 * Controlled: `value` undefined means no calendar, and every edit goes through `onChange`.
 */
const CalendarDialog = ({
  open,
  title,
  note,
  grain,
  value,
  issues = [],
  onChange,
  onRemove,
  onClose,
}: {
  open: boolean
  title: string
  note: string
  grain: Grain
  value: Calendar | undefined
  issues?: string[]
  onChange: (next: Calendar | undefined) => void
  onRemove: () => void
  onClose: () => void
}) => {
  const titleId = useId()
  const dark = useAppTheme().mode === 'dark'
  const colors = {
    text: dark ? '#FFFFFF' : '#003F61',
    invalid: dark ? '#F44336' : '#D32F2F',
    bg: dark ? '#162645' : '#FAFBFD',
  }
  const muiTheme = useTheme()
  const narrow = useMediaQuery(muiTheme.breakpoints.down('sm'))
  // The editor header sits at z-index 9999 over the canvas. The dialog and the hour lists it opens go above it.
  const aboveHeader = useMemo(
    () => ({ ...muiTheme, zIndex: { ...muiTheme.zIndex, modal: 10010 } }),
    [muiTheme]
  )
  const textSx = { fontFamily: 'Rubik, sans-serif', fontSize: '14px' }

  return (
    <ThemeProvider theme={aboveHeader}>
      <Dialog
        open={open}
        onClose={onClose}
        fullScreen={narrow}
        fullWidth
        dir="rtl"
        aria-labelledby={titleId}
        slotProps={{
          paper: {
            sx: { backgroundColor: colors.bg, backgroundImage: 'none' },
          },
        }}
      >
        <DialogTitle
          id={titleId}
          sx={{ fontFamily: 'Rubik, sans-serif', color: colors.text }}
        >
          {title}
        </DialogTitle>
        <DialogContent>
          <Typography
            sx={{
              ...textSx,
              color: colors.text,
              opacity: 0.8,
              marginBottom: 2,
            }}
          >
            {note}
          </Typography>
          {issues.map(issue => (
            <Typography
              key={issue}
              role="alert"
              sx={{ ...textSx, color: colors.invalid, marginBottom: 1 }}
            >
              {issue}
            </Typography>
          ))}
          <CalendarEditor
            value={value ?? []}
            onChange={onChange}
            grain={grain}
          />
        </DialogContent>
        <DialogActions sx={{ justifyContent: 'flex-start', gap: 1 }}>
          <Button
            onClick={onRemove}
            disabled={!value}
            color="error"
            sx={{ fontFamily: 'Rubik, sans-serif' }}
          >
            הסר לוח זמנים
          </Button>
          <Button
            onClick={onClose}
            variant="contained"
            sx={{
              fontFamily: 'Rubik, sans-serif',
              bgcolor: '#72A4EB',
              '&:hover': { bgcolor: '#5A8FD4' },
            }}
          >
            סגירה
          </Button>
        </DialogActions>
      </Dialog>
    </ThemeProvider>
  )
}

export default CalendarDialog
